import * as React from 'react'

import { useAuth } from '@/contexts/auth-context'
import { channelUnreadService } from '@/services/channel-unread.service'

type UnreadMap = Record<string, number>

interface ForumUnreadContextValue {
  unreadMap: UnreadMap
  totalUnreadCount: number
  getUnread: (channelId?: string) => number
  resetChannel: (channelId?: string) => Promise<void>
  resetAll: () => Promise<void>
  isLoading: boolean
}

const ForumUnreadContext = React.createContext<ForumUnreadContextValue | undefined>(undefined)

export const useForumUnread = (): ForumUnreadContextValue => {
  const context = React.useContext(ForumUnreadContext)
  if (!context) {
    throw new Error('useForumUnread must be used within a ForumUnreadProvider')
  }
  return context
}

const buildStorageKey = (userId?: number | string) => `forum:unread:${userId || 'guest'}`

export const ForumUnreadProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth()
  const [unreadMap, setUnreadMap] = React.useState<UnreadMap>({})
  const [isLoading, setIsLoading] = React.useState(false)

  const totalUnreadCount = React.useMemo(() => {
    return Object.values(unreadMap).reduce((sum, count) => sum + count, 0)
  }, [unreadMap])

  React.useEffect(() => {
    if (!user?.id) {
      setUnreadMap({})
      return
    }

    const loadUnreadCounts = async () => {
      setIsLoading(true)
      try {
        const counts = await channelUnreadService.getAllUnreadCounts()
        setUnreadMap(counts)
      } catch (error) {
        const key = buildStorageKey(user.id)
        try {
          const raw = typeof window !== 'undefined' ? window.localStorage.getItem(key) : null
          if (raw) {
            const parsed = JSON.parse(raw) as UnreadMap
            setUnreadMap(parsed)
          }
        } catch {
          setUnreadMap({})
        }
      } finally {
        setIsLoading(false)
      }
    }

    loadUnreadCounts()
  }, [user?.id])

  React.useEffect(() => {
    if (!user?.id) return

    const key = buildStorageKey(user.id)
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(key, JSON.stringify(unreadMap))
      }
    } catch {
      // eslint-disable-next-line no-console
      console.error('Failed to persist unread counts to localStorage')
    }
  }, [unreadMap, user?.id])

  const getUnread = React.useCallback((channelId?: string) => (channelId ? unreadMap[channelId] || 0 : 0), [unreadMap])

  const resetChannel = React.useCallback(async (channelId?: string) => {
    if (!channelId) return

    try {
      await channelUnreadService.resetUnreadCount(channelId)

      setUnreadMap(prev => {
        if (!prev[channelId]) return prev
        const next = { ...prev }
        delete next[channelId]
        return next
      })
    } catch (error) {
      setUnreadMap(prev => {
        if (!prev[channelId]) return prev
        const next = { ...prev }
        delete next[channelId]
        return next
      })
    }
  }, [])

  const resetAll = React.useCallback(async () => {
    try {
      await channelUnreadService.resetAllUnreadCounts()
      setUnreadMap({})
    } catch (error) {
      setUnreadMap({})
    }
  }, [])

  const value = React.useMemo(
    () => ({
      unreadMap,
      totalUnreadCount,
      getUnread,
      resetChannel,
      resetAll,
      isLoading,
    }),
    [unreadMap, totalUnreadCount, getUnread, resetChannel, resetAll, isLoading]
  )

  return <ForumUnreadContext.Provider value={value}>{children}</ForumUnreadContext.Provider>
}
